import { ArrowRight } from 'lucide-react'
import { motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import HeaderLanguageSelect from './HeaderLanguageSelect'
import { useI18nSafe } from '../context/I18nContext'

export default function WebsiteEntrance({ label = 'Loading...', onDismiss }) {
  const { language, setLanguage } = useI18nSafe()
  const reduceMotion = useReducedMotion()
  const videoSrc = '/videos/1.mp4'
  const [allowBackgroundVideo, setAllowBackgroundVideo] = useState(false)
  const [videoPoster, setVideoPoster] = useState('/slides/slide1-640.webp')

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const updatePoster = () => {
      setVideoPoster(
        window.innerWidth >= 1440
          ? '/slides/slide1-1920.webp'
          : window.innerWidth >= 768
          ? '/slides/slide1-1280.webp'
          : '/slides/slide1-640.webp'
      )
    }

    updatePoster()
    window.addEventListener('resize', updatePoster)
    return () => window.removeEventListener('resize', updatePoster)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || reduceMotion) {
      setAllowBackgroundVideo(false)
      return
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    const saveData = Boolean(connection?.saveData)
    const networkType = String(connection?.effectiveType || '').toLowerCase()
    const isSlowNetwork = networkType === 'slow-2g' || networkType === '2g'
    const isNarrowScreen = window.matchMedia('(max-width: 767px)').matches

    setAllowBackgroundVideo(!saveData && !isSlowNetwork && !isNarrowScreen)
  }, [reduceMotion])

  useEffect(() => {
    if (!onDismiss || typeof window === 'undefined') return undefined

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onDismiss()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onDismiss])

  const copy = useMemo(
    () =>
        language === 'fr'
          ? {
              eyebrow: 'Bienvenue',
              titleLine1: 'Une entrée',
              titleLine2: ['industrielle', 'premium'],
              description:
                'Chariots élévateurs, gerbeurs, pièces de rechange et support client réunis dans une entrée plus nette et plus élégante.',
              enter: 'Entrer',
            }
          : {
              eyebrow: 'Welcome',
              titleLine1: 'A refined',
              titleLine2: ['industrial', 'entry'],
              description:
                'Forklifts, stackers, spare parts, and support presented through a cleaner, more premium first impression.',
              enter: 'Enter',
            },
    [language]
  )

  const languageButtons = useMemo(
    () => [
      { code: 'fr', label: 'FR' },
      { code: 'en', label: 'EN' },
    ],
    []
  )
  const languageOptions = useMemo(
    () => languageButtons.map((item) => ({ value: item.code, label: item.label })),
    [languageButtons]
  )
  const titleLine2 = Array.isArray(copy.titleLine2) ? copy.titleLine2 : [copy.titleLine2]

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex min-h-dvh items-center justify-center overflow-y-auto bg-[var(--dash-blacker)] px-5 py-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-white sm:px-8"
      initial={{ opacity: 0, scale: reduceMotion ? 1 : 1.025, filter: reduceMotion ? 'none' : 'blur(18px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, scale: reduceMotion ? 1 : 1.01, filter: reduceMotion ? 'none' : 'blur(8px)' }}
      transition={{ duration: reduceMotion ? 0.15 : 0.65, ease: [0.22, 1, 0.36, 1] }}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      {!reduceMotion && allowBackgroundVideo ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.video
            className="absolute inset-0 h-full w-full object-cover object-[50%_30%] opacity-70 saturate-[1.08] contrast-[1.04] md:object-center"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            poster={videoPoster}
            initial={{ opacity: 0, scale: 1.035 }}
            animate={{ opacity: 0.7, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <source src={videoSrc} type="video/mp4" />
          </motion.video>
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,4,6,0.82)_0%,rgba(4,4,6,0.48)_28%,rgba(4,4,6,0.38)_72%,rgba(4,4,6,0.82)_100%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,93,16,0.18)_0%,transparent_42%)]" />
        </div>
      ) : null}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,93,16,0.12)_0%,transparent_42%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.06)_0%,transparent_22%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03)_0%,transparent_24%,transparent_76%,rgba(255,255,255,0.02)_100%)]" />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(#ffffff 0.55px, transparent 0.55px)',
            backgroundSize: '36px 36px',
          }}
        />
        <motion.div
          className="absolute left-[-8rem] top-[-6rem] h-[24rem] w-[24rem] rounded-full bg-[var(--dash-main)]/14 blur-[110px]"
          animate={
            reduceMotion
              ? { opacity: 0.16 }
              : { x: [0, 32, 0], y: [0, 20, 0], opacity: [0.14, 0.24, 0.14] }
          }
          transition={
            reduceMotion
              ? { duration: 0.2 }
              : { duration: 14, repeat: Infinity, ease: 'easeInOut' }
          }
        />
        <motion.div
          className="absolute bottom-[-7rem] right-[-7rem] h-[22rem] w-[22rem] rounded-full bg-[var(--dash-other)]/10 blur-[120px]"
          animate={
            reduceMotion
              ? { opacity: 0.12 }
              : { x: [0, -26, 0], y: [0, -14, 0], opacity: [0.1, 0.18, 0.1] }
          }
          transition={
            reduceMotion
              ? { duration: 0.2 }
              : { duration: 16, repeat: Infinity, ease: 'easeInOut' }
          }
        />
      </div>

      <motion.div
        className="relative my-auto flex w-full max-w-3xl flex-col items-center justify-center text-center"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0, y: reduceMotion ? 0 : 18 },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              staggerChildren: reduceMotion ? 0 : 0.1,
              delayChildren: reduceMotion ? 0 : 0.26,
            },
          },
        }}
      >
        <motion.div
          className="relative flex items-center justify-center"
          variants={{
            hidden: { opacity: 1, scale: reduceMotion ? 1 : 0.94, y: reduceMotion ? 0 : 8 },
            visible: {
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: reduceMotion ? 0.15 : 0.75, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          <div className="absolute inset-[-2.5rem] rounded-full bg-[radial-gradient(circle_at_center,rgba(255,93,16,0.16)_0%,rgba(255,93,16,0.08)_36%,transparent_72%)] blur-3xl" />
          <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
            <motion.div
              className="absolute inset-y-[-30%] left-[-45%] w-1/3 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/35 to-transparent blur-md mix-blend-screen"
              animate={reduceMotion ? { x: '0%', opacity: 0 } : { x: ['0%', '430%'], opacity: [0, 0.7, 0] }}
              transition={
                reduceMotion
                  ? { duration: 0.1 }
                  : { duration: 3.8, repeat: Infinity, ease: 'linear', repeatDelay: 0.8 }
              }
            />
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logos/logo.svg"
            alt="ETC Company"
            width="468"
            height="212"
            className="relative h-16 w-auto max-w-[70vw] select-none object-contain drop-shadow-[0_14px_36px_rgba(0,0,0,0.45)] sm:h-20 lg:h-24"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>

        <motion.div
          variants={{
            hidden: { opacity: 0, y: reduceMotion ? 0 : 14 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: reduceMotion ? 0.15 : 0.65, ease: [0.22, 1, 0.36, 1] },
            },
          }}
          className="mt-6 flex flex-col gap-4 sm:mt-8 sm:gap-5"
        >
          <div className="mx-auto inline-flex items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-white/72 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--dash-main)] shadow-[0_0_12px_rgba(255,93,16,0.55)]" />
            <span>{copy.eyebrow}</span>
          </div>

          <h1
            className="max-w-2xl pb-1 text-3xl font-light leading-[1.02] tracking-[-0.02em] text-white sm:text-5xl lg:text-6xl"
            style={{ lineHeight: 1.02 }}
          >
            {copy.titleLine1}
            <span className="mt-2 block bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text font-black text-transparent [text-shadow:0_8px_30px_rgba(255,93,16,0.24)]">
              {titleLine2.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </span>
          </h1>

          <p className="mx-auto max-w-xl text-sm leading-6 text-slate-300 sm:text-base sm:leading-7">{copy.description}</p>
        </motion.div>

        <motion.div
          className="mt-7 flex w-full flex-col items-center gap-3 sm:mt-9 sm:flex-row sm:justify-center sm:gap-4"
          initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0.15 : 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeaderLanguageSelect
            value={language}
            onChange={setLanguage}
            options={languageOptions}
            className="w-[92px] shrink-0"
            triggerClassName="h-10"
            panelClassName="min-w-[92px]"
          />
          <button
            type="button"
            onClick={() => onDismiss?.()}
            className="group relative inline-flex min-h-11 shrink-0 items-center gap-3 overflow-hidden rounded-full bg-[#c2410c] px-7 py-3 text-sm font-bold text-white shadow-[0_12px_28px_rgba(194,65,12,0.28)] transition-colors hover:bg-[var(--dash-other)] hover:text-[var(--dash-black)] active:scale-95"
          >
            <span className="relative z-10">{copy.enter}</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>

        <motion.div
          className="mt-12 h-px w-32 overflow-hidden bg-white/10"
          variants={{
            hidden: { opacity: 0, scaleX: 0 },
            visible: {
              opacity: 1,
              scaleX: 1,
              transition: { duration: reduceMotion ? 0.1 : 0.7, ease: [0.22, 1, 0.36, 1] },
            },
          }}
          style={{ transformOrigin: 'center' }}
        >
          <motion.div
            className="h-full w-1/2 bg-gradient-to-r from-transparent via-[var(--dash-main)] to-transparent shadow-[0_0_12px_var(--dash-main)]"
            animate={{ x: ['-100%', '300%'], opacity: [0.25, 1, 0.25] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  )
}
